/**
 * Лента грузов диспетчера и сводка городов отправления — блок `WEB-2.2`,
 * требование 401 (и половиной 402: цена в строке ленты).
 *
 * Лента читает `listCargoListings` страницами и показывает **каждое**
 * объявление ответа в порядке ответа — занятые и без свободных машин тоже: ни
 * сортировки, ни отбора (форма F4). Строка — маршрут, дата погрузки и срок
 * выгрузки, свободные машины из всех, груз, вес, кузов подписью, ADR, цена
 * суммой и валютой ответа (CR-003 §1), км, «фирма проверена» и статус своего
 * offer'а подписью при `my_offer`. Формы и кнопок подачи в ленте нет: условие
 * подачи живёт на экране объявления, и «Подробнее» ведёт туда (решение формы Б3).
 *
 * Фильтры уходят ровно именами параметров контракта, незаполненное не уходит
 * (решение формы Б5): типы кузова — по параметру на выбранный тип, ADR и
 * температура — выбором «не важно / да / нет», города — идентификатором
 * подсказки справочника. Граница цены уходит с выбранной валютой, и умолчания
 * у валюты нет (решение формы Б7, долг `TD-190`): цена без валюты уходит как
 * есть, и сервер отвечает `422` по полю `currency` — сообщение ложится под
 * выбором валюты. Даты, пределы и валюту клиент не проверяет.
 *
 * Сводка городов отправления — `listCargoListingMapCities` с теми же
 * фильтрами, **без** страницы и курсора (решение формы Б6): строки «город —
 * число» в порядке ответа и «всего» из ответа, а не суммой. Нажатие на город
 * ставит фильтр города погрузки и перечитывает ленту и сводку. Карты и внешнего
 * адреса нет; отказ сводки встаёт на месте сводки и ленту не уносит.
 *
 * Состояние встаёт по единственному правилу места (долг `TD-66`). Смена языка
 * не стирает фильтров (долг `TD-72`): экран переподписывается на месте.
 *
 * Блок `WEB-D.2`, решение `Б1`: форма фильтров лежит в закрытом
 * `details.disclosure`, состав и порядок полей прежние. Отказ, чьё сообщение
 * легло под полем, блок раскрывает: сообщение не должно быть спрятано.
 *
 * Решение `Б14`: сводка городов лежит в своём закрытом `details.disclosure`, чтобы
 * первый груз был виден без прокрутки. Подпись блока — заголовок сводки. Состояние
 * сводки встаёт внутри `citiesSection`: так оно не стирает `summary`. Отказ сводки
 * блок раскрывает, как у фильтров и автопарка: отказ не должен быть спрятан.
 */

import { ApiFailure } from '../api/client';
import type { ApiClient } from '../api/client';
import { nextPageCursor } from '../api/contract';
import type { BodyTypeInfo, CargoFeedFilters, CargoListing, City, ListingMapCityList } from '../api/contract';
import { CURRENCIES } from '../api/generated/enums';
import { activeLocale, bodyTypeStringKey, cargoOfferStatusStringKey, currencyStringKey } from '../i18n';
import type { Strings } from '../i18n';
import { cargoFeedListingPath } from '../router';
import {
  createFieldMessageSlot,
  dateLabel,
  enumLabel,
  FIELD_MESSAGE_CLASS,
  moneyLabel,
  placeState,
  refusalText,
  renderFieldMessages,
  screenStateFor,
} from './state';
import type { ScreenState } from './state';

export interface CargoFeedScreenOptions {
  readonly client: ApiClient;
  readonly strings: Strings;
  readonly companyId: string;
}

export interface CargoFeedScreen {
  readonly element: HTMLElement;
  /** Состояние экрана либо `null` — содержимое показано. */
  readonly state: ScreenState | null;
  load(): Promise<void>;
  /** Подписи на новом языке; введённые фильтры остаются (долг `TD-72`). */
  relabel(next: Strings): void;
}

/** Значения выбора «да» и «нет» у логического фильтра — строкой провода. */
const YES_CHOICE = String(true);
const NO_CHOICE = String(false);

/** Подпись первого пункта выбора валюты: «не выбрано» без слова. */
const NO_CURRENCY_CAPTION = '—';

/** Значение порождённого перечня, совпавшее с выбранным, либо `null`. */
function pick<T extends string>(values: readonly T[], chosen: string): T | null {
  for (const value of values) {
    if (value === chosen) {
      return value;
    }
  }

  return null;
}

/** Подпись города в подсказке: имя на языке ответа и страна под ним. */
function cityCaption(city: City): string {
  return `${city.name} (${city.country_name})`;
}

function cityIn(cities: readonly City[], id: string): City | null {
  for (const city of cities) {
    if (String(city.id) === id) {
      return city;
    }
  }

  return null;
}

export function createCargoFeedScreen(options: CargoFeedScreenOptions): CargoFeedScreen {
  const { client, companyId } = options;
  let strings = options.strings;

  const element = document.createElement('section');
  const heading = document.createElement('h1');
  const filtersBlock = document.createElement('details');
  const filtersCaption = document.createElement('summary');
  const form = document.createElement('form');
  const notice = document.createElement('p');
  const citiesBlock = document.createElement('details');
  const citiesCaption = document.createElement('summary');
  const citiesSection = document.createElement('section');
  const citiesList = document.createElement('ul');
  const citiesTotal = document.createElement('p');
  const list = document.createElement('ul');
  const more = document.createElement('button');

  element.className = 'screen screen-cargo-feed';
  filtersBlock.className = 'disclosure';
  form.className = 'form cargo-feed-filters';
  form.noValidate = true;
  notice.className = 'notice';
  notice.setAttribute('role', 'alert');
  citiesBlock.className = 'disclosure';
  citiesBlock.append(citiesCaption, citiesSection);
  citiesSection.className = 'cargo-feed-cities';
  citiesTotal.className = 'cargo-feed-city-total';
  more.type = 'button';
  more.className = 'action';

  /** Подписи экрана: каждая ставится одной записью — и при сборке, и при смене языка. */
  const captions: Array<() => void> = [];

  function caption(apply: () => void): void {
    captions.push(apply);
    apply();
  }

  function labelled(control: HTMLElement, text: () => string, extra: readonly HTMLElement[] = []): HTMLLabelElement {
    const label = document.createElement('label');
    const shown = document.createElement('span');

    label.className = 'form-field';
    shown.className = 'form-field__caption';
    caption(() => {
      shown.textContent = text();
    });
    label.append(shown, ...extra, control, createFieldMessageSlot());

    return label;
  }

  function input(name: string, type: string): HTMLInputElement {
    const node = document.createElement('input');

    node.name = name;
    node.type = type;

    return node;
  }

  /** Логический фильтр: «не важно» (пустое — не уходит), «да», «нет». */
  function triState(name: string): HTMLSelectElement {
    const select = document.createElement('select');
    const choices: Array<[string, () => string]> = [
      ['', () => strings.cargo_feed_any],
      [YES_CHOICE, () => strings.cargo_feed_yes],
      [NO_CHOICE, () => strings.cargo_feed_no],
    ];

    select.name = name;

    for (const [value, text] of choices) {
      const choice = document.createElement('option');

      choice.value = value;
      caption(() => {
        choice.textContent = text();
      });
      select.append(choice);
    }

    return select;
  }

  // ── Город: подсказка справочника по строке поиска ───────────────────────

  interface CityField {
    readonly select: HTMLSelectElement;
    readonly search: HTMLInputElement;
    readonly label: HTMLLabelElement;
    /** Выбранный город — остаётся вариантом и тогда, когда новый поиск его не вернул. */
    chosen: City | null;
    shown: readonly City[];
    /** Поколение поиска: ответ на устаревшую строку к выбору не применяется. */
    generation: number;
  }

  function cityField(name: string, text: () => string): CityField {
    const select = document.createElement('select');
    // Строка поиска имени не несёт: в запрос она не уходит — уходит выбранный
    // город идентификатором справочника.
    const search = document.createElement('input');

    select.name = name;
    search.type = 'search';
    caption(() => {
      search.placeholder = strings.cargo_listing_form_city_search;
    });

    const field: CityField = {
      select,
      search,
      label: labelled(select, text, [search]),
      chosen: null,
      shown: [],
      generation: 0,
    };

    search.addEventListener('input', () => {
      void suggest(field);
    });
    select.addEventListener('change', () => {
      field.chosen = cityIn(field.shown, select.value);
    });
    fillCities(field, []);

    return field;
  }

  /** Варианты — ответ справочника в его порядке; выбранный город не теряется. */
  function fillCities(field: CityField, found: readonly City[]): void {
    const cities: City[] = [];
    const chosen = field.chosen;

    if (chosen !== null && cityIn(found, String(chosen.id)) === null) {
      cities.push(chosen);
    }

    cities.push(...found);
    field.shown = cities;

    const choices: HTMLOptionElement[] = [document.createElement('option')];

    for (const city of cities) {
      const choice = document.createElement('option');

      choice.value = String(city.id);
      choice.textContent = cityCaption(city);
      choices.push(choice);
    }

    field.select.replaceChildren(...choices);
    field.select.value = chosen === null ? '' : String(chosen.id);
    // Блок `WEB-D.2`, решение `Б4`: пока выбирать нечего, на экране один ввод поиска.
    field.select.hidden = cities.length === 0;
  }

  async function suggest(field: CityField): Promise<void> {
    const query = field.search.value;

    if (query.trim() === '') {
      return;
    }

    field.generation += 1;

    const mine = field.generation;
    const slot = field.label.querySelector(`.${FIELD_MESSAGE_CLASS}`);

    try {
      const found = await client.listCities({ q: query });

      if (mine !== field.generation) {
        return;
      }

      if (slot !== null) {
        slot.textContent = '';
      }

      fillCities(field, found);
    } catch (error) {
      // Годность строки поиска судит сервер; его отказ — текстом под полем.
      if (mine === field.generation && slot !== null) {
        slot.textContent = refusalText(error, strings);
      }
    }
  }

  // ── Поля фильтров — имена параметров контракта ──────────────────────────

  const origin = cityField('origin_city_id', () => strings.cargo_listing_form_origin);
  const destination = cityField('destination_city_id', () => strings.cargo_listing_form_destination);
  const loadingFrom = input('loading_from', 'text');
  const loadingTo = input('loading_to', 'text');
  const vehiclesMin = input('vehicles_min', 'number');
  const weightFrom = input('weight_from', 'number');
  const weightTo = input('weight_to', 'number');
  const bodyArea = document.createElement('fieldset');
  const bodyLegend = document.createElement('legend');
  const temperature = triState('temperature_controlled');
  const adr = triState('adr');
  const priceFrom = input('price_from', 'text');
  const priceTo = input('price_to', 'text');
  const currency = document.createElement('select');
  const keyword = input('q', 'search');
  const applyButton = document.createElement('button');
  const resetButton = document.createElement('button');

  priceFrom.inputMode = 'decimal';
  priceTo.inputMode = 'decimal';
  bodyArea.className = 'form-field cargo-feed-body-types';
  applyButton.type = 'submit';
  applyButton.className = 'action';
  resetButton.type = 'button';
  resetButton.className = 'action';

  // Валюта: первый пункт пуст и выбран — умолчания нет (решение формы Б7,
  // долг `TD-190`); прочие — порождённый перечень контракта целиком.
  currency.name = 'currency';

  const noCurrency = document.createElement('option');

  noCurrency.value = '';
  noCurrency.textContent = NO_CURRENCY_CAPTION;
  currency.append(noCurrency);

  for (const value of CURRENCIES) {
    const choice = document.createElement('option');

    choice.value = value;
    caption(() => {
      choice.textContent = enumLabel(strings, currencyStringKey(value));
    });
    currency.append(choice);
  }

  caption(() => {
    loadingFrom.placeholder = strings.cargo_feed_date_hint;
    loadingTo.placeholder = strings.cargo_feed_date_hint;
    heading.textContent = strings.cargo_feed_title;
    filtersCaption.textContent = strings.cargo_feed_filters;
    bodyLegend.textContent = strings.cargo_feed_body_type;
    applyButton.textContent = strings.cargo_feed_apply;
    resetButton.textContent = strings.cargo_feed_reset;
    more.textContent = strings.cargo_feed_more;
    citiesCaption.textContent = strings.cargo_feed_cities_title;
  });

  form.append(
    origin.label,
    destination.label,
    labelled(loadingFrom, () => strings.cargo_feed_loading_from),
    labelled(loadingTo, () => strings.cargo_feed_loading_to),
    labelled(vehiclesMin, () => strings.cargo_feed_vehicles_min),
    labelled(weightFrom, () => strings.cargo_feed_weight_from),
    labelled(weightTo, () => strings.cargo_feed_weight_to),
    bodyArea,
    labelled(temperature, () => strings.cargo_feed_temperature),
    labelled(adr, () => strings.cargo_feed_adr),
    labelled(priceFrom, () => strings.cargo_feed_price_from),
    labelled(priceTo, () => strings.cargo_feed_price_to),
    labelled(currency, () => strings.cargo_feed_currency),
    labelled(keyword, () => strings.cargo_feed_q),
    applyButton,
    resetButton,
  );
  filtersBlock.append(filtersCaption, form);

  // ── Типы кузова: справочник сервера, по флажку на запись ────────────────

  /** Флажок и тип, который он называет, — тип берётся из записи справочника. */
  let bodyBoxes: ReadonlyArray<readonly [HTMLInputElement, BodyTypeInfo['type']]> = [];

  function fillBodyTypes(types: readonly BodyTypeInfo[]): void {
    const boxes: Array<readonly [HTMLInputElement, BodyTypeInfo['type']]> = [];
    const labels: HTMLElement[] = [bodyLegend];

    // Порядок и имена — ответа справочника, на языке ответа (`WEB-2.1` К4).
    for (const { type, name } of types) {
      const box = input('body_type', 'checkbox');
      const label = document.createElement('label');
      const shown = document.createElement('span');

      box.value = type;
      shown.textContent = name;
      label.className = 'form-field form-field--check';
      label.append(box, shown, createFieldMessageSlot());
      labels.push(label);
      boxes.push([box, type]);
    }

    bodyBoxes = boxes;
    bodyArea.replaceChildren(...labels);
  }

  /** Значение логического фильтра: «не важно» — нет параметра. */
  function triStateOf(select: HTMLSelectElement): boolean | undefined {
    if (select.value === YES_CHOICE) {
      return true;
    }

    if (select.value === NO_CHOICE) {
      return false;
    }

    return undefined;
  }

  /** Строка поля без пробелов по краям; пустое — «не задано» (`undefined`). */
  function entered(node: HTMLInputElement | HTMLSelectElement): string | undefined {
    const raw = node.value.trim();

    return raw === '' ? undefined : raw;
  }

  function numberOf(node: HTMLInputElement | HTMLSelectElement): number | undefined {
    const raw = entered(node);

    return raw === undefined ? undefined : Number(raw);
  }

  /**
   * Фильтры формы — ровно параметры контракта; незаполненное не уходит вовсе
   * (решение формы Б5). Граница цены и валюта уходят независимо: цена без
   * валюты — `422` сервера, валюта без цены ничего не сужает; клиент их не
   * связывает и не проверяет ни дат, ни пределов.
   */
  function collect(): CargoFeedFilters {
    const originId = numberOf(origin.select);
    const destinationId = numberOf(destination.select);
    const from = entered(loadingFrom);
    const to = entered(loadingTo);
    const vehicles = numberOf(vehiclesMin);
    const lightest = numberOf(weightFrom);
    const heaviest = numberOf(weightTo);
    const controlled = triStateOf(temperature);
    const dangerous = triStateOf(adr);
    const cheapest = entered(priceFrom);
    const dearest = entered(priceTo);
    const chosenCurrency = pick(CURRENCIES, currency.value);
    const words = entered(keyword);
    const types: Array<BodyTypeInfo['type']> = [];

    for (const [box, type] of bodyBoxes) {
      if (box.checked) {
        types.push(type);
      }
    }

    return {
      ...(originId === undefined ? {} : { origin_city_id: originId }),
      ...(destinationId === undefined ? {} : { destination_city_id: destinationId }),
      ...(from === undefined ? {} : { loading_from: from }),
      ...(to === undefined ? {} : { loading_to: to }),
      ...(vehicles === undefined ? {} : { vehicles_min: vehicles }),
      ...(lightest === undefined ? {} : { weight_from: lightest }),
      ...(heaviest === undefined ? {} : { weight_to: heaviest }),
      ...(types.length === 0 ? {} : { body_type: types }),
      ...(controlled === undefined ? {} : { temperature_controlled: controlled }),
      ...(dangerous === undefined ? {} : { adr: dangerous }),
      ...(cheapest === undefined ? {} : { price_from: cheapest }),
      ...(dearest === undefined ? {} : { price_to: dearest }),
      ...(chosenCurrency === null ? {} : { currency: chosenCurrency }),
      ...(words === undefined ? {} : { q: words }),
    };
  }

  // ── Состояние экрана ────────────────────────────────────────────────────

  let state: ScreenState | null = 'loading';
  /** Пришёл ли ответ сервера — величина правила места состояния. */
  let answered = false;
  let listings: readonly CargoListing[] = [];
  let cursor: string | null = null;
  /** Фильтры последнего применения: «Показать ещё» уходит с ними же. */
  let applied: CargoFeedFilters = {};
  let cities: ListingMapCityList | null = null;
  let citiesState: ScreenState | null = 'loading';
  /** Поколение чтения: ответ на устаревшие фильтры к экрану не применяется. */
  let generation = 0;
  let loadingMore = false;

  function row(listing: CargoListing): HTMLElement {
    const node = document.createElement('li');
    const facts = document.createElement('span');
    const details = document.createElement('a');
    const locale = activeLocale();
    const parts: string[] = [
      `${listing.origin} → ${listing.destination}`,
      `${strings.cargo_listing_form_loading_at}: ${dateLabel(locale, listing.loading_at)}`,
      `${strings.cargo_listing_form_unload_by}: ${dateLabel(locale, listing.unload_by)}`,
      `${strings.cargo_listing_slots_free}: ${listing.slots_free} ${strings.cargo_listing_slots_of} ${listing.slots_total}`,
      listing.cargo_type,
      `${listing.weight_kg} ${strings.cargo_listing_kg}`,
      enumLabel(strings, bodyTypeStringKey(listing.body.type)),
    ];

    if (listing.adr) {
      parts.push(strings.cargo_feed_adr);
    }

    if (listing.price !== null) {
      parts.push(moneyLabel(strings, listing.price));
    }

    parts.push(`${listing.distance_km} ${strings.cargo_listing_km}`);

    if (listing.company_verified) {
      parts.push(strings.cargo_feed_company_verified);
    }

    const mine = listing.my_offer;

    if (mine !== null) {
      parts.push(`${strings.cargo_feed_my_offer}: ${enumLabel(strings, cargoOfferStatusStringKey(mine.status))}`);
    }

    node.className = 'cargo-feed-row';
    facts.textContent = parts.join(' · ');
    details.className = 'company-link';
    details.href = cargoFeedListingPath(companyId, listing.id);
    details.textContent = strings.cargo_feed_details;
    node.append(facts, details);

    return node;
  }

  function cityRow(city: City, count: number): HTMLElement {
    const node = document.createElement('li');
    const choose = document.createElement('button');

    node.className = 'cargo-feed-city-row';
    choose.type = 'button';
    choose.className = 'action';
    choose.textContent = `${city.name} — ${count}`;
    choose.addEventListener('click', () => {
      void chooseOrigin(city);
    });
    node.append(choose);

    return node;
  }

  function renderCities(): void {
    const rows: HTMLElement[] = [];

    for (const { city, count } of cities?.data ?? []) {
      rows.push(cityRow(city, count));
    }

    citiesList.replaceChildren(...rows);
    citiesTotal.textContent = cities === null ? '' : `${strings.cargo_feed_cities_total}: ${cities.meta.total}`;

    // Своё место у сводки: её отказ встаёт на месте её строк, лента цела.
    placeState(
      citiesSection,
      {
        state: citiesState,
        answered: true,
        frame: [],
        list: citiesList,
        after: cities === null ? [] : [citiesTotal],
      },
      strings,
    );
  }

  function render(): void {
    const rows: HTMLElement[] = [];

    for (const listing of listings) {
      rows.push(row(listing));
    }

    list.replaceChildren(...rows);
    renderCities();

    const after: HTMLElement[] = [];

    if (cursor !== null) {
      after.push(more);
    }

    placeState(
      element,
      {
        state,
        answered,
        frame: [heading, filtersBlock, notice, citiesBlock],
        list,
        after,
        emptyCaption: strings.cargo_feed_empty,
      },
      strings,
    );
  }

  /**
   * Сводка городов с теми же фильтрами; её отказ — своё состояние. Ответ не
   * ложится на экран сам: возвращается действие, которое его кладёт, и
   * вызывающий решает, применять ли его (при отказе ленты сводка остаётся
   * прежней — ответ на ревью Р2).
   */
  async function readCities(filters: CargoFeedFilters): Promise<() => void> {
    try {
      const received = await client.listCargoListingMapCities(filters);

      return () => {
        cities = received;
        citiesState = screenStateFor({ kind: 'done', empty: received.data.length === 0 });
      };
    } catch (error) {
      const code = error instanceof ApiFailure ? error.code : null;

      return () => {
        cities = null;
        citiesState = screenStateFor({ kind: 'failed', code });
        citiesBlock.open = true;
      };
    }
  }

  async function load(): Promise<void> {
    generation += 1;

    const mine = generation;

    state = screenStateFor({ kind: 'pending' });
    answered = false;
    listings = [];
    cursor = null;
    cities = null;
    citiesState = screenStateFor({ kind: 'pending' });
    applied = collect();
    notice.textContent = '';
    render();

    const citiesRead = readCities(applied);

    try {
      const [types, page] = await Promise.all([client.listBodyTypes(), client.listCargoListings(applied)]);

      if (mine !== generation) {
        return;
      }

      fillBodyTypes(types);
      listings = page.data;
      cursor = nextPageCursor(page.meta);
      answered = true;
      state = screenStateFor({ kind: 'done', empty: listings.length === 0 });
    } catch (error) {
      if (mine !== generation) {
        return;
      }

      listings = [];
      cursor = null;
      answered = false;
      state = screenStateFor({
        kind: 'failed',
        code: error instanceof ApiFailure ? error.code : null,
      });
    }

    const placeCities = await citiesRead;

    if (mine !== generation) {
      return;
    }

    placeCities();
    render();
  }

  /**
   * Перечитать первую страницу ленты и сводку с данными фильтрами. Отказ ленты
   * прежней страницы не стирает: сообщения сервера ложатся под полями, которые
   * он назвал, непоказанное — общим текстом над лентой; введённое остаётся.
   * Применёнными фильтры становятся только с ответом ленты, и сводка ложится
   * тогда же: при отказе «Показать ещё» и сводка остаются при той странице,
   * которая на экране (ответ на ревью Р2).
   */
  async function reread(filters: CargoFeedFilters): Promise<void> {
    generation += 1;

    const mine = generation;

    notice.textContent = '';
    renderFieldMessages(form, []);

    const citiesRead = readCities(filters);

    try {
      const page = await client.listCargoListings(filters);

      if (mine !== generation) {
        return;
      }

      applied = filters;
      listings = page.data;
      cursor = nextPageCursor(page.meta);
      state = screenStateFor({ kind: 'done', empty: listings.length === 0 });

      const placeCities = await citiesRead;

      if (mine !== generation) {
        return;
      }

      placeCities();
    } catch (error) {
      if (mine !== generation) {
        return;
      }

      const failure = error instanceof ApiFailure ? error : null;
      const details = failure?.details ?? [];
      const homeless = renderFieldMessages(form, details);
      const texts: string[] = [];

      // Сообщение легло под полем — блок раскрывается; общий текст стоит вне блока.
      if (homeless.length < details.length) {
        filtersBlock.open = true;
      }

      for (const detail of homeless) {
        texts.push(detail.message);
      }

      if (texts.length === 0) {
        texts.push(refusalText(error, strings));
      }

      notice.textContent = texts.join(' ');
    }

    render();
  }

  /** Город сводки становится фильтром города погрузки. */
  async function chooseOrigin(city: City): Promise<void> {
    origin.chosen = city;
    fillCities(origin, origin.shown);
    await reread(collect());
  }

  async function loadMore(): Promise<void> {
    if (cursor === null || loadingMore) {
      return;
    }

    loadingMore = true;
    more.disabled = true;

    const mine = generation;

    try {
      // Следующая страница — с теми же фильтрами, что дали первую.
      const page = await client.listCargoListings(applied, { cursor });

      if (mine === generation) {
        listings = [...listings, ...page.data];
        cursor = nextPageCursor(page.meta);
      }
    } catch (error) {
      notice.textContent = refusalText(error, strings);
    } finally {
      loadingMore = false;
      more.disabled = false;
    }

    render();
  }

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void reread(collect());
  });

  resetButton.addEventListener('click', () => {
    form.reset();
    origin.chosen = null;
    fillCities(origin, []);
    destination.chosen = null;
    fillCities(destination, []);
    void reread(collect());
  });

  more.addEventListener('click', () => {
    void loadMore();
  });

  function relabel(next: Strings): void {
    strings = next;

    for (const apply of captions) {
      apply();
    }

    render();
  }

  render();

  return {
    element,

    get state(): ScreenState | null {
      return state;
    },

    load,
    relabel,
  };
}
